import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { CompanyBrand } from '@/components/shell/CompanyBrand';
import { UserMenu } from '@/components/shell/UserMenu';
import { LegalMarkdown } from '@/components/legal/LegalMarkdown';
import { formatDateOnly } from '@/lib/format';
import {
  LegalAcceptanceRefusedError,
  useAcceptLegal,
  useLegalAcceptance,
  type LegalAcceptance,
  type LegalStatus,
} from '@/api/useLegal';

/**
 * The SaaS Terms of Service and Privacy Policy, read and accepted (M2-48,
 * Kyle 2026-10-24).
 *
 * <b>Where:</b> inside the authenticated shell (`AppLayout`), driven by
 * `/me`'s `legal`:
 *
 * - `null` (the gate is off in this environment) or `Accepted`: nothing.
 * - `DueSoon` (a material update not yet in effect): a banner, until the
 *   date, offering to review and accept now. The screen it opens can be left.
 * - `Required` (never accepted, or a material update in effect): this screen,
 *   full-screen, in place of the whole shell — no navigation, no page. The
 *   header keeps the user menu, so someone who will not accept can sign out.
 *
 * <b>Enforcement is the browser's only</b> (the API records; CC-28 adds the
 * API's own check). A state this build does not know is shown as nothing
 * rather than guessed at: the gate fails open, never into a blank app.
 *
 * Every word the person agrees to is the API's: the documents' text, and the
 * line beside the box (an Owner's adds "on behalf of {company}"). The box is
 * ticked for THAT wording and those versions — if a refusal re-reads them
 * and they changed, it is no longer ticked.
 */
export function LegalAcceptanceScreen({
  mode,
  onClose,
}: {
  /** `required`: the blocking gate. `review`: opened from the banner, and can be left. */
  mode: 'required' | 'review';
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const acceptance = useLegalAcceptance(true);
  const heading = useRef<HTMLHeadingElement>(null);
  // The heading names what the documents ARE (new, or updated), so it waits
  // for them; it takes focus once it is there, so a screen reader starts at it.
  const loading = acceptance.isPending;
  useEffect(() => {
    if (!loading) heading.current?.focus();
  }, [loading]);

  const data = acceptance.data;
  const updated = data ? data.documents.some((d) => d.acceptedVersion !== null) : false;
  const title = mode === 'review' || updated ? t('legal.gate.updatedTitle') : t('legal.gate.title');

  return (
    <div
      className="flex min-h-screen flex-col bg-[var(--color-background)]"
      data-testid="legal-gate"
    >
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-[var(--color-background)] px-4">
        <div className="min-w-0">
          <CompanyBrand />
        </div>
        <div className="ml-auto">
          <UserMenu />
        </div>
      </header>
      <main
        className="mx-auto w-full max-w-3xl flex-1 space-y-6 px-4 py-6 sm:py-8"
        aria-labelledby={`${id}-title`}
      >
        {loading ? null : (
          <div className="space-y-2">
            <h1
              id={`${id}-title`}
              ref={heading}
              tabIndex={-1}
              className="text-xl font-semibold focus:outline-none"
            >
              {title}
            </h1>
            <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="legal-intro">
              {mode === 'review' && data?.dueOn
                ? t('legal.gate.reviewIntro', { date: formatDateOnly(data.dueOn) })
                : updated
                  ? t('legal.gate.updatedIntro')
                  : t('legal.gate.intro')}
            </p>
          </div>
        )}

        {acceptance.isPending ? (
          <LoadingState label={t('legal.gate.loading')} />
        ) : acceptance.isError || !data ? (
          <ErrorState
            title={t('legal.gate.loadErrorTitle')}
            onRetry={() => void acceptance.refetch()}
          />
        ) : (
          <AcceptForm acceptance={data} mode={mode} onClose={onClose} />
        )}
      </main>
    </div>
  );
}

function AcceptForm({
  acceptance,
  mode,
  onClose,
}: {
  acceptance: LegalAcceptance;
  mode: 'required' | 'review';
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const accept = useAcceptLegal();
  // What was ticked: the line's version and every document's. Read again
  // with anything different, and the box is clear.
  const shown = [
    acceptance.statement.version,
    ...acceptance.documents.map((d) => `${d.kind}:${d.version}`),
  ].join('|');
  const [tickedFor, setTickedFor] = useState<string | null>(null);
  const [unticked, setUnticked] = useState(false);
  const ticked = tickedFor === shown;
  const alertRef = useRef<HTMLParagraphElement>(null);

  const refusal =
    accept.error instanceof LegalAcceptanceRefusedError
      ? accept.error.message
      : accept.error
        ? t('legal.gate.failed')
        : null;
  const message = unticked && !ticked ? t('legal.gate.tickRequired') : refusal;

  useEffect(() => {
    if (message) alertRef.current?.focus();
  }, [message]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (accept.isPending) return;
    accept.reset();
    if (!ticked) {
      setUnticked(true);
      return;
    }
    setUnticked(false);
    accept.mutate({
      agreed: true,
      statementVersion: acceptance.statement.version,
      documents: acceptance.documents.map((d) => ({ kind: d.kind, version: Number(d.version) })),
    });
  }

  return (
    <>
      {acceptance.documents.map((doc) => (
        <div key={doc.kind} className="space-y-2" data-testid="legal-document">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h2 className="text-base font-semibold">{doc.title}</h2>
            <p className="text-xs text-[var(--color-muted-foreground)]" data-testid="legal-version">
              {t('legal.gate.version', {
                version: Number(doc.version),
                date: formatDateOnly(doc.effectiveOn),
              })}
              {doc.state === 'Accepted' ? ` · ${t('legal.gate.alreadyAccepted')}` : ''}
            </p>
          </div>
          {/* A scroll container is focusable (M1-42), so the keyboard can read it. */}
          <div
            role="region"
            aria-label={doc.title}
            tabIndex={0}
            className="max-h-[45vh] overflow-y-auto rounded-md border border-[var(--color-border)] p-4 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
            data-testid="legal-text"
          >
            <LegalMarkdown source={doc.text} />
          </div>
        </div>
      ))}

      <form noValidate onSubmit={submit} className="space-y-4">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0"
            checked={ticked}
            disabled={accept.isPending}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? `${id}-error` : undefined}
            onChange={(e) => {
              setTickedFor(e.target.checked ? shown : null);
              if (e.target.checked) setUnticked(false);
            }}
          />
          <span data-testid="legal-statement">{acceptance.statement.text}</span>
        </label>
        {message ? (
          <p
            id={`${id}-error`}
            ref={alertRef}
            role="alert"
            tabIndex={-1}
            className="text-sm text-[var(--color-destructive)] focus:outline-none"
            data-testid="legal-error"
          >
            {message}
          </p>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            type="submit"
            className="h-11 w-full text-base sm:w-auto"
            disabled={accept.isPending}
          >
            {accept.isPending ? t('legal.gate.accepting') : t('legal.gate.accept')}
          </Button>
          {mode === 'review' && onClose ? (
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full text-base sm:w-auto"
              disabled={accept.isPending}
              onClick={onClose}
            >
              {t('legal.gate.notNow')}
            </Button>
          ) : null}
        </div>
      </form>
    </>
  );
}

/** Until a material update takes effect: what is coming, when, and a way to accept now (Kyle, Q4-B). */
export function LegalUpdateBanner({
  status,
  onReview,
}: {
  status: LegalStatus;
  onReview: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="mb-6 flex flex-col gap-3 rounded-md border-l-4 border-[var(--color-primary)] bg-[var(--color-muted)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid="legal-banner"
    >
      <p className="text-sm">
        {status.dueOn
          ? t('legal.banner.dueOn', { date: formatDateOnly(status.dueOn) })
          : t('legal.banner.due')}
      </p>
      <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={onReview}>
        {t('legal.banner.review')}
      </Button>
    </div>
  );
}
